import { retryFileOperation } from '@risksmart-app/components/src/file/retryFileOperation';
import {
  pickActionableFileFailure,
  settleFileOperations,
} from '@risksmart-app/components/src/file/settleFileOperations';
import type {
  FileUploadResponse,
  UseFileUpload,
} from '@risksmart-app/components/src/file/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { isAxiosError } from 'axios';

import { useTRPC } from '../utils/trpc';

// Bare axios client for the presigned S3 PUT: no app Authorization header and
// no Sentry response interceptor, so a failed upload can't ship the signed URL
// (signature, credential params) to an error tracker. Mirrors the web twin
// (`packages/web/src/hooks/mutations/file/useFileUploadTRPC.tsx`).
const s3Client = axios.create();

// S3 validates the Content-Type against the signature.
const uploadToS3 = async (presignedUrl: string, file: File) => {
  return await s3Client.put(presignedUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
  });
};

/**
 * tRPC twin of `@risksmart-app/components/src/file/useFileUpload` (V2 REST).
 * Same callable signature and lifecycle: presign registers PENDING carrying
 * each file's content type and meta, then the S3 PUTs run per file and ONE
 * batched save completes every file whose bytes landed. Each operation gets
 * the shared bounded retry policy.
 *
 * The PUT fails per file, the save is atomic. Files that uploaded are saved
 * BEFORE any PUT failure is thrown, so a partial S3 failure does not discard
 * work that succeeded.
 *
 * The portal only attaches files to one questionnaire-response parent (no
 * change-request path), so the web twin's change-request operation is omitted
 * here.
 *
 * Consumers must import the flag-gated `useFileUpdate` wrapper, never this.
 */
export const useFileUploadTRPC = (): UseFileUpload => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const presignMutation = useMutation({
    ...trpc.frontend.files.createPresignedUploadUrls.mutationOptions(),
  });
  const saveMutation = useMutation({
    ...trpc.frontend.files.save.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.files.getById.queryKey(),
        });
      },
    }),
  });

  return async ({
    parentType,
    parentIds,
    newFiles = [],
  }): Promise<FileUploadResponse> => {
    try {
      const presignedUrls =
        newFiles.length > 0
          ? await retryFileOperation(() =>
              presignMutation.mutateAsync({
                parentType,
                files: newFiles.map((f) => ({
                  FileName: f.name,
                  ContentType: f.type,
                  Meta: f.meta,
                })),
              })
            )
          : [];

      const uploads = await settleFileOperations(
        presignedUrls.map(async (url, index) => {
          const file = newFiles[index];
          await retryFileOperation(() => uploadToS3(url.signedUrl, file));

          return { file, url };
        })
      );

      const filesToSave = uploads.values.map(({ file, url }) => ({
        FileId: url.key,
        FileName: file.name,
        FileSize: file.size,
        ContentType: file.type,
        Meta: file.meta,
        ChangeRequestFileOperation: null,
      }));

      if (filesToSave.length > 0) {
        await retryFileOperation(() =>
          saveMutation.mutateAsync({
            ParentIds: parentIds,
            ParentType: parentType,
            Files: filesToSave,
          })
        );
      }

      if (uploads.failures.length > 0) {
        throw pickActionableFileFailure(uploads.failures);
      }

      return { fileIds: filesToSave.map((file) => file.FileId) };
    } catch (error) {
      // V2 parity: mask raw axios messages (the S3 PUT failure). tRPC errors
      // pass through untouched so downstream error handling can read them.
      if (isAxiosError(error)) {
        throw new Error('Something went wrong');
      }
      throw error;
    }
  };
};
